// Packages
import { Buffer } from 'node:buffer';

import { phc_api } from '@global-client/logic/core/api';
import { buffersAreEqual, handleErrorCodes, handleErrors, requestOptions } from '@global-client/logic/utils/mssUtils';

import { type Api } from '@hub-client/logic/core/apiCore';
// Logic
import { createLogger } from '@hub-client/logic/logging/Logger';
import { assert } from '@hub-client/logic/utils/assert';

// Models
import { type AttrKeyResp } from '@global-client/models/MSS/TAuths';
import { type ErrorCode, type Result, ResultResponse, type SignedIdentifyingAttrs } from '@global-client/models/MSS/TGeneral';
import * as TPHC from '@global-client/models/MSS/TPHC';
import UserSecretManager, { type AttrKeysFetcher } from '@global-client/models/MSS/UserSecret';

// Stores
import { useGlobal } from '@global-client/stores/global';

const logger = createLogger('PHCServer');

export default class PHCServer {
	private readonly _phcAPI: Api;
	private _userStateObjects: Record<string, TPHC.UserObjectDetails> | undefined;
	/** NOTE: Do not use this variable directly to prevent using an expired authToken. Instead, use _getAuthToken(). */
	private _authToken: string | null = null;
	private _expiryAuthToken: null | bigint = null;
	/** Holds everything to do with the user secret; see UserSecret.ts. */
	private readonly _userSecretManager: UserSecretManager;

	constructor() {
		if (!phc_api) {
			throw new Error('PHC API is null.');
		}
		this._phcAPI = phc_api;
		this._userSecretManager = new UserSecretManager(this);
		const savedAuthToken = localStorage.getItem('PHauthToken');
		if (savedAuthToken) {
			const authToken: TPHC.AuthTokenPackage = JSON.parse(savedAuthToken);
			this._authToken = authToken.auth_token;
			this._expiryAuthToken = authToken.expires;
		}
	}

	/**
	 * Drop everything this instance holds for the account that was logged in.
	 *
	 * The store keeps the instance across a logout, so anything left behind here goes on being used:
	 * an auth token would authorise requests for the account that just left, and a cached user secret
	 * would be handed out instead of the logout procedure being triggered. Reading the token back from
	 * local storage - which is what this used to do - defeats the purpose, whichever order the caller
	 * clears the two in.
	 */
	reset() {
		this._userStateObjects = undefined;
		this._authToken = null;
		this._expiryAuthToken = null;
		this._userSecretManager.forgetUserSecret();
	}

	triggerLogoutProcedure() {
		const global = useGlobal();
		global.logout({ key: 'login.login_again' });
	}

	// #region Global client login

	/**
	 * Contact the PHC welcome endpoint to get basic details about the current PubHubs setup.
	 *
	 * @returns Information about the PubHubs constellation and a list of hubs that are running.
	 */
	async welcome() {
		return await handleErrors<TPHC.WelcomeRespPHC>(() => this._phcAPI.apiGET<TPHC.PHCWelcomeResp>(this._phcAPI.apiURLS.welcome));
	}

	async cardPseudoPackage(): Promise<TPHC.CardPseudRespSucces> {
		const options = {
			headers: {
				'Content-Type': 'application/json',
				Authorization: (await this._getAuthToken()) ?? '',
			},
			method: 'POST',
		};
		const CardPsFn = () => this._phcAPI.api<Result<TPHC.CardPseudResp, ErrorCode>>(this._phcAPI.apiURLS.CardPseudPackage, options);

		const okCardPseudResp = await handleErrors<TPHC.CardPseudResp>(CardPsFn);
		if (ResultResponse.Success in okCardPseudResp) {
			return okCardPseudResp.Success;
		} else if ('RetryWithNewAuthToken' in okCardPseudResp) {
			throw new Error('Retreiving the Pseudo card package failed — retry with a new Authtoken.');
		} else {
			throw new Error('Unexpected response in OK CardPseudResponse');
		}
	}

	private _setAuthToken(authTokenPackage: TPHC.AuthTokenPackage) {
		this._authToken = authTokenPackage.auth_token;
		this._expiryAuthToken = authTokenPackage.expires;
		localStorage.setItem('PHauthToken', JSON.stringify(authTokenPackage));
	}

	/**
	 * Handle the response from the EnterEP.
	 *
	 * @param enterResp The response from the EnterEP that needs to be handled.
	 * @returns An object with a boolean denoting whether the user successfully entered PubHubs and an errorMessage.
	 * The errorMessage is null if the user successfully entered, an object with a translation key and a list of values to use in the translation otherwise.
	 */
	private _handleEnterResp(
		enterResp: TPHC.EnterResp,
	):
		| { entered: false; errorMessage: { key: string; values?: string[] }; enterResp: null }
		| { entered: true; errorMessage: null; enterResp: [TPHC.Attr, TPHC.AttrAddStatus][] } {
		if (enterResp === 'AccountDoesNotExist') {
			return { entered: false, errorMessage: { key: 'errors.account_does_not_exist' }, enterResp: null };
		} else if (enterResp === 'Banned') {
			return { entered: false, errorMessage: { key: 'errors.banned' }, enterResp: null };
		} else if (enterResp === 'NoBannableAttribute') {
			return { entered: false, errorMessage: { key: 'errors.general_error' }, enterResp: null };
		} else if (enterResp === 'RetryWithNewIdentifyingAttr') {
			return { entered: false, errorMessage: { key: 'errors.retry_with_new_attr' }, enterResp: null };
		} else if ('AttributeBanned' in enterResp) {
			return { entered: false, errorMessage: { key: 'errors.attribute_banned', values: [enterResp.AttributeBanned.value] }, enterResp: null };
		} else if ('AttributeAlreadyTaken' in enterResp && enterResp.AttributeAlreadyTaken.not_identifying) {
			return {
				entered: false,
				errorMessage: { key: 'errors.notid_attribute_already_taken', values: [enterResp.AttributeAlreadyTaken.value] },
				enterResp: null,
			};
		} else if ('AttributeAlreadyTaken' in enterResp) {
			return {
				entered: false,
				errorMessage: { key: 'errors.attribute_already_taken', values: [enterResp.AttributeAlreadyTaken.value] },
				enterResp: null,
			};
		} else if ('RetryWithNewAddAttr' in enterResp) {
			return { entered: false, errorMessage: { key: 'errors.retry_with_new_attr' }, enterResp: null };
		} else if ('Entered' in enterResp) {
			const { auth_token_package } = enterResp.Entered;
			const authToken = handleErrorCodes<TPHC.AuthTokenPackage, TPHC.AuthTokenDeniedReason>(auth_token_package);
			this._setAuthToken(authToken);
			return { entered: true, errorMessage: null, enterResp: enterResp.Entered.attr_status };
		} else {
			throw new Error('Unknown response from the enter endpoint.');
		}
	}

	/**
	 * Request to enter PubHubs, depending on the enterMode registering a new account or logging into an existing account.
	 *
	 * @param identifyingAttr The attribute identifying the user when registering or logging in.
	 * @param addAttrs The attributes that have to be added to the account, required when registering a new account or when no bannable attribute is registered for this account.
	 * @param enterMode The mode determines whether we want to create an account if none exists and whether we expect an account to exist.
	 * @returns An object with a boolean to know whether the user successfully entered PubHubs or not and an error message (which is null if no error occured).
	 */
	public async enter(
		addAttrs: string[],
		enterMode: TPHC.PHCEnterMode,
		identifyingAttr?: string,
		registerOnlyWithUniqueAttrs: boolean = false,
	): Promise<
		| { entered: true; errorMessage: null; enterResp: [TPHC.Attr, TPHC.AttrAddStatus][] }
		| { entered: false; errorMessage: { key: string; values?: string[] }; enterResp: null }
	> {
		const requestPayload: TPHC.PHCEnterReq = {
			identifying_attr: identifyingAttr,
			mode: enterMode,
			add_attrs: addAttrs,
			register_only_with_unique_attrs: registerOnlyWithUniqueAttrs,
		};
		let authorization: string | undefined;
		if (!identifyingAttr) authorization = await this._getAuthToken();
		const okEnterResp = await handleErrors(() =>
			this._phcAPI.api<TPHC.PHCEnterResp>(this._phcAPI.apiURLS.enter, requestOptions<TPHC.PHCEnterReq>(requestPayload, authorization)),
		);
		return this._handleEnterResp(okEnterResp);
	}

	/**
	 * Call the refreshEP to refresh an (expired) authToken.
	 *
	 * @returns true if token was successfully refreshed, false if user needs to re-authenticate (triggers logout)
	 * @throws Will throw an error if the request to refresh the authToken was denied (for example because the user is logging in with a banned attribute).
	 */
	async refreshEP(): Promise<boolean> {
		assert.isNotNull(this._authToken, 'An (expired) authToken is needed to call the refreshEP.');
		const options = {
			headers: { Authorization: this._authToken },
			method: 'GET',
		};
		const okRefreshResp = await handleErrors<TPHC.RefreshResp>(() => this._phcAPI.api<TPHC.PHCRefreshResp>(this._phcAPI.apiURLS.refresh, options));
		if (okRefreshResp === 'ReobtainAuthToken') {
			this.triggerLogoutProcedure();
			return false;
		} else if ('Denied' in okRefreshResp) {
			switch (okRefreshResp.Denied) {
				case TPHC.AuthTokenDeniedReason.Banned:
					throw new Error('The user is trying to login with a banned attribute.');
				case TPHC.AuthTokenDeniedReason.NoBannableAttribute:
					throw new Error('The user does not have a bannable attribute.');
				default:
					throw new Error('Unknown reason to deny an auth token.');
			}
		} else if ('Success' in okRefreshResp) {
			this._setAuthToken(okRefreshResp.Success);
			return true;
		} else {
			throw new Error('Unknown response from the refresh endpoint.');
		}
	}

	/**
	 * Attempts to refresh the auth token and retry an operation.
	 * Use this when receiving RetryWithNewAuthToken from an endpoint.
	 *
	 * @returns true if token was refreshed (caller should retry), false if logout was triggered
	 */
	private async _refreshAndRetry(): Promise<boolean> {
		logger.info('Received RetryWithNewAuthToken, attempting to refresh token...');
		return await this.refreshEP();
	}

	private async _getAuthToken(): Promise<string | undefined> {
		if (!this._authToken) {
			this.triggerLogoutProcedure();
			return;
		}
		// Convert Date.now() to represent the number of seconds from 1970-01-01T00:00:00Z UTC, to be able to compare it to the expiry timestamp we get from the AuthTokenPackage.
		// 5 minutes leeway are built in (if a token expires in the next 5 minutes, it will get refreshed).
		const leeway_minutes: number = 5;
		const now: bigint = BigInt(Math.floor(Date.now() / 1000));
		const expiry: bigint | null = this._expiryAuthToken ? BigInt(this._expiryAuthToken) : null;
		if (expiry && expiry <= now + BigInt(leeway_minutes * 60)) {
			await this.refreshEP();
		}
		return this._authToken;
	}

	// #endregion

	// #region UserSecret object

	// The user secret logic lives in UserSecret.ts, which documents how the `usersecret` and
	// `usersecretbackup` objects are meant to be written and read. The methods below only forward to it.

	getUserSecretInfo() {
		return this._userSecretManager.getUserSecretInfo();
	}

	getUserSecretObject() {
		return this._userSecretManager.getUserSecretObject();
	}

	resolveUserSecretObject(signedIdentifyingAttrs: SignedIdentifyingAttrs, requestAttrKeys: AttrKeysFetcher, afterReset: boolean = false) {
		return this._userSecretManager.resolveUserSecretObject(signedIdentifyingAttrs, requestAttrKeys, afterReset);
	}

	storeUserSecretObject(
		attrKeyResp: Record<string, AttrKeyResp>,
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecretObject: TPHC.UserSecretObject | null,
		userSecretObjectDetails: { usersecret: TPHC.UserObjectDetails; backup: TPHC.UserObjectDetails | null } | null,
	) {
		return this._userSecretManager.storeUserSecretObject(attrKeyResp, identifyingAttrs, userSecretObject, userSecretObjectDetails);
	}

	addIdentifyingAttrToUserSecret(
		attrKeyResp: Record<string, AttrKeyResp>,
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecretObject: TPHC.UserSecretObject | null,
		userSecretObjectDetails: { usersecret: TPHC.UserObjectDetails; backup: TPHC.UserObjectDetails | null } | null,
	) {
		return this._userSecretManager.addIdentifyingAttr(attrKeyResp, identifyingAttrs, userSecretObject, userSecretObjectDetails);
	}

	// #endregion

	// #region Encrypt and decrypt

	/**
	 * Concatenate the Uint8Arrays.
	 *
	 * @param arrays A list of Uint8Arrays that should be concatenated.
	 * @returns A single Uint8Array in which the arrays are concatenated.
	 */
	private _concatUint8Arrays(arrays: Uint8Array[]) {
		// Compute how long the concatenatedArray should be
		let newLength = 0;
		for (const array of arrays) {
			newLength += array.length;
		}

		// Create a new Uint8Array of the correct length and concatenate the arrays
		const concatenatedArray = new Uint8Array(newLength);

		let offset = 0;
		for (const array of arrays) {
			concatenatedArray.set(array, offset);
			offset += array.length;
		}

		return concatenatedArray;
	}

	/**
	 * Encrypt the data to store it at PubHubs Central.
	 *
	 * @param data The data that will be encrypted.
	 * @param key The key to encrypt the data.
	 * @returns The ciphertext of the encrypted data.
	 */
	async encryptData(data: Uint8Array, key: Uint8Array) {
		const encoder = new TextEncoder();
		// Generate random 256 bits (32 bytes) data
		const randomBits = crypto.getRandomValues(new Uint8Array(32));
		// Append the key to the random 256 bits
		// These random bits are generated and added before the encoded key to make sure that the key used to encrypt the data is different every time.
		// We need a different key every time, because there is a limit on how many times the encrypt function can be invoked with the same key when using RBG-based IV construction,
		// as written in section 8.3 of NIST Special Publication 800-38D (https://nvlpubs.nist.gov/nistpubs/legacy/sp/nistspecialpublication800-38d.pdf).
		// Having a different key every time means that there is a negligible chance of using the same key with the same iv twice.
		// The IV could have been constructed of just random bits, but this would mean that we need to store an extra 32 bytes for every object to store the IV with the encrypted data.
		const derivedKey = this._concatUint8Arrays([randomBits, key, encoder.encode('key')]);
		const derivedIV = this._concatUint8Arrays([randomBits, key, encoder.encode('iv')]);
		// Calculate the SHA-256 hash of the concatenated random bits with the key to use as AES key and IV
		const aesKeyHash = await crypto.subtle.digest('SHA-256', derivedKey);
		const iv = await crypto.subtle.digest('SHA-256', derivedIV);
		// Import the key and use it to encrypt the data
		const aesKey = await crypto.subtle.importKey('raw', aesKeyHash, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
		const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, data as BufferSource);
		// Prepend the random bits to the ciphertext
		const cipherText = this._concatUint8Arrays([randomBits, new Uint8Array(encryptedData)]);
		return cipherText;
	}

	private async _decrypt(ciphertext: Uint8Array, encodedKey: Uint8Array) {
		// Extract the random bits of data (that were used to generate the seed) from the ciphertext
		const randomBits = ciphertext.slice(0, 32);
		const encoder = new TextEncoder();
		const seedKey = this._concatUint8Arrays([randomBits, encodedKey, encoder.encode('key')]);
		const seedIV = this._concatUint8Arrays([randomBits, encodedKey, encoder.encode('iv')]);
		// Calculate the SHA-256 hash of the concatenated random bits with the key to use as AES key and the SHA-512 hash to use as IV
		const aesKeyHash = await crypto.subtle.digest('SHA-256', seedKey);
		const iv = await crypto.subtle.digest('SHA-256', seedIV);
		// Import the key and use it to decrypt the data
		const aesKey = await crypto.subtle.importKey('raw', aesKeyHash, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
		const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv }, aesKey, ciphertext.slice(32));
		return new Uint8Array(decryptedData);
	}

	/**
	 * Decrypt the data that is stored at PubHubs Central.
	 *
	 * @param ciphertext The data that will be decrypted.
	 * @param key The key to decrypt the data.
	 * @returns The plaintext of the decrypted data.
	 */
	async decryptData(ciphertext: Uint8Array, key: string) {
		const encodingStrategies = [() => new Uint8Array(Buffer.from(key, 'base64')), () => new TextEncoder().encode(key)];
		for (let i = 0; i < encodingStrategies.length; i++) {
			try {
				const encodedKey = encodingStrategies[i]();
				return await this._decrypt(ciphertext, encodedKey);
			} catch (error) {
				logger.error(`Could not decrypt data with encoding version ${i}`, error);
			}
		}
		logger.error('Failed to decrypt the data.');
		throw new Error('Failed to decrypt the data.');
	}

	// #endregion

	// #region User object store

	/**
	 * Request the state of the current user.
	 *
	 * @returns The state of the current user, or undefined if logout was triggered.
	 */
	public async stateEP() {
		const maxRetries = 1;
		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			const options = {
				headers: { Authorization: (await this._getAuthToken()) ?? '' },
				method: 'GET',
			};
			const okStateResp = await handleErrors<TPHC.StateResp>(() => this._phcAPI.api<TPHC.PHCStateResp>(this._phcAPI.apiURLS.state, options));
			if (okStateResp === 'RetryWithNewAuthToken') {
				if (attempt < maxRetries && (await this._refreshAndRetry())) {
					continue; // Retry with new token
				}
				return; // Logout was triggered or max retries reached
			} else if ('State' in okStateResp) {
				this._userStateObjects = okStateResp.State.stored_objects;
				return okStateResp.State;
			} else {
				throw new Error('Unknown response from the state endpoint.');
			}
		}
	}

	/**
	 * Retrieve a user object with a given hash from PubHubs central.
	 * Authorization happens by using the HMAC from the userObjectDetails, so the access token is not included in the headers of the request.
	 *
	 * @param objectDetails The details on the object stored at pubhubs central.
	 * @param handle The handle of the object that is requested.
	 * @returns The requested object if it exists.
	 */
	private async _getObjectEP(objectDetails: TPHC.UserObjectDetails, handle: string): Promise<ArrayBuffer | null> {
		const maxAttempts = 3;
		let details = objectDetails;
		for (let attempts = 0; attempts < maxAttempts; attempts++) {
			const getObjResp = await handleErrors<TPHC.GetObjectRespProblem>(() =>
				this._phcAPI.apiGET<TPHC.PHCGetObjectResp | ArrayBuffer>(this._phcAPI.apiURLS.getObject + '/' + details.hash + '/' + details.hmac),
			);
			if (getObjResp instanceof ArrayBuffer) {
				return getObjResp;
			} else if (getObjResp === TPHC.GetObjectRespProblem.NotFound || getObjResp === TPHC.GetObjectRespProblem.RetryWithNewHmac) {
				// Both problems mean the object details we sent are stale; an outdated hmac, or a hash that
				// has since been replaced - and PHC documents the same remedy for both (see GetObjectResp in
				// pubhubs/src/api/phc.rs) - reload the stored objects from the state endpoint and retry with
				// the details found there.
				if (attempts === maxAttempts - 1) {
					throw new Error(`Could not retrieve the object with handle ${handle}, errorcode: ${getObjResp}`);
				}
				await this.stateEP();
				const refreshedDetails = await this._getObjectDetails(handle);
				// If the object cannot be found, return null.
				if (!refreshedDetails) {
					return null;
				}
				details = refreshedDetails;
				continue;
			} else {
				throw new Error('Unknown response from the getObjectEP.');
			}
		}
		throw new Error('Unexpectedly could not handle the response of the getObjectEP.');
	}

	private async _getObjectDetails(handle: string): Promise<TPHC.UserObjectDetails | undefined> {
		if (this._userStateObjects === undefined) {
			const state = await this.stateEP();
			if (!state) {
				return undefined; // Auth failed, logout already triggered
			}
		}

		assert.isDefined(this._userStateObjects, 'Could not retrieve the user state.');
		const objectDetails: TPHC.UserObjectDetails | undefined = this._userStateObjects[handle];
		return objectDetails;
	}

	/**
	 * Try to retrieve a user object from PubHubs Central.
	 *
	 * @param handle The handle of the object that needs to be retrieved.
	 * @returns The object and the corresponding object details if the object exists; null otherwise.
	 */
	async getUserObject(handle: string) {
		const objectDetails = await this._getObjectDetails(handle);
		if (!objectDetails) {
			return null;
		}
		const object = await this._getObjectEP(objectDetails, handle);
		return { object, details: objectDetails };
	}

	async getDecryptedUserObject(handle: string) {
		// Retrieve the contents of the object and the userSecret
		const getObjectResp = await this.getUserObject(handle);
		if (!getObjectResp?.object) {
			return null;
		}
		const object = new Uint8Array(getObjectResp.object);
		const userSecret = await this.getUserSecretInfo();
		// this._getUserSecret will only return undefined in case where the user gets logged out because they were messing with their local storage.
		// In practice, this means that this._getUserSecret will never return undefined, since the user will have been redirected to the login page in that case.
		assert.isDefined(userSecret, 'Could not retrieve the userSecret from localstorage.');
		// Decrypt the data that was stored under the given handle
		const decryptedData = await this.decryptData(object, userSecret);

		// Decode the data
		const decoder = new TextDecoder();
		const decodedData = decoder.decode(decryptedData);
		return decodedData;
	}

	/**
	 * Write a new object with a handle to the object store.
	 *
	 * @param handle The handle to store the object under.
	 * @param object The object to write to the object store.
	 * @returns True if the object was stored correctly.
	 */
	private async _newObjectEP(handle: string, object: Uint8Array): Promise<boolean> {
		const maxAttempts = 3;
		let tokenRefreshed = false;
		for (let attempts = 0; attempts < maxAttempts; attempts++) {
			const options = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/octet-stream',
					Authorization: (await this._getAuthToken()) ?? '',
				},
				body: object as unknown as Uint8Array,
			};
			const newObjectResp = await handleErrors<TPHC.StoreObjectResp>(() =>
				this._phcAPI.api<TPHC.PHCStoreObjectResp>(this._phcAPI.apiURLS.newObject + '/' + handle, options),
			);
			switch (newObjectResp) {
				case 'PleaseRetry':
					if (attempts === maxAttempts - 1) {
						throw new Error('Max attemps for RetryFromStart were passed.');
					}
					continue;
				case 'RetryWithNewAuthToken':
					if (!tokenRefreshed && (await this._refreshAndRetry())) {
						tokenRefreshed = true;
						continue; // Retry with new token
					}
					return false; // Logout was triggered or already retried
				case 'MissingHash': {
					// There is already an object stored under this handle, so use overwriteObjectEP instead
					const existingObject = await this.getUserObject(handle);
					if (!existingObject) {
						throw new Error('Could not find the object, even though getting a MissingHash response from newObjectEP.');
					}
					return await this._overwriteObjectEP(handle, existingObject.details.hash, object);
				}
				case 'NotFound':
					throw new Error('Unexpected response NotFound for a newObjectEP request.');
				case 'HashDidNotMatch':
					throw new Error('Unexpected response HashDidNotMatch for a newObjectEP request.');
				case 'NoChanges':
					throw new Error('Unexpected response NoChanges for a newObjectEP request.');
				default:
					if ('QuotumReached' in newObjectResp) {
						throw new Error(`Could not store the new user object with handle ${handle}, because the quotum is reached.`);
					} else if ('Stored' in newObjectResp) {
						assert.isDefined(
							this._userStateObjects,
							'this._userStateObjects cannot be undefined, unless something went wrong with the call to the stateEP.',
						);
						this._userStateObjects = newObjectResp.Stored.stored_objects;
						return true;
					} else {
						throw new Error('Unknown response for newObjectEP request.');
					}
			}
		}
		throw new Error('Unexpectedly could not handle the response of the newObjectEP.');
	}

	/**
	 * Overwrite an object with a certain hash in the object store.
	 * @param handle The handle of the object to overwrite.
	 * @param overwriteHash The hash of the object to overwrite.
	 * @param object The new contents of the object.
	 * @returns True if the object was stored correctly.
	 */
	private async _overwriteObjectEP(handle: string, overwriteHash: string, object: Uint8Array): Promise<boolean> {
		const maxAttempts = 3;
		let hash = overwriteHash;
		let tokenRefreshed = false;
		for (let attempts = 0; attempts < maxAttempts; attempts++) {
			const options = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/octet-stream',
					Authorization: (await this._getAuthToken()) ?? '',
				},
				body: object as unknown as Uint8Array,
			};
			const overwriteObjectResp = await handleErrors<TPHC.StoreObjectResp>(() =>
				this._phcAPI.api<TPHC.PHCStoreObjectResp>(this._phcAPI.apiURLS.overwriteObject + '/' + handle + '/' + hash, options),
			);
			switch (overwriteObjectResp) {
				case 'PleaseRetry':
					if (attempts === maxAttempts - 1) {
						throw new Error('Max attemps for RetryFromStart were passed');
					}
					continue;
				case 'RetryWithNewAuthToken':
					if (!tokenRefreshed && (await this._refreshAndRetry())) {
						tokenRefreshed = true;
						continue; // Retry with new token
					}
					return false; // Logout was triggered or already retried
				case 'MissingHash':
					throw new Error('Unexpected response MissingHash for a newObjectEP request.');
				case 'NotFound':
					return await this._newObjectEP(handle, object);
				case 'HashDidNotMatch': {
					if (attempts === maxAttempts) {
						throw new Error('The object stored at this handle has a different hash');
					}
					await this.stateEP();
					const existingObject = await this.getUserObject(handle);
					if (existingObject !== null) {
						hash = existingObject.details.hash;
						continue;
					}
					throw new Error('Could not find the object.');
				}
				case 'NoChanges':
					return true;
				default:
					if ('QuotumReached' in overwriteObjectResp) {
						throw new Error(`Could not store the new user object with handle ${handle}, because the quotum is reached.`);
					} else if ('Stored' in overwriteObjectResp) {
						assert.isDefined(
							this._userStateObjects,
							'this._userStateObjects cannot be undefined, unless something went wrong with the call to the stateEP.',
						);
						this._userStateObjects = overwriteObjectResp.Stored.stored_objects;
						return true;
					} else {
						throw new Error('Unknown response for newObjectEP request.');
					}
			}
		}
		throw new Error('Unexpectedly could not handle the response of the overwriteEP.');
	}

	/**
	 * Stores an object at the PHC object store.
	 *
	 * @param handle The handle of the object to store.
	 * @param data The data of the object to store.
	 * @param overwriteHash The hash of the object if it existed before.
	 * @returns A boolean value, denoting whether the object was stored correctly or not.
	 */
	async storeObject(handle: string, data: Uint8Array, overwriteHash?: string): Promise<boolean> {
		let stored: boolean | undefined;
		if (overwriteHash) {
			stored = await this._overwriteObjectEP(handle, overwriteHash, data);
		} else {
			stored = await this._newObjectEP(handle, data);
		}

		if (!stored || stored === undefined) {
			return false;
		}
		const storedObject = await this.getUserObject(handle);
		assert.isNotNull(storedObject, `Could not retrieve the object with handle ${handle}, even though it seemed to be stored correctly.`);
		assert.isNotNull(
			storedObject.object,
			`Could not retrieve the object with handle ${handle}, even though it seemed to be stored correctly and the object details were retrieved.`,
		);
		assert.isTrue(buffersAreEqual(storedObject.object, data), `The data for the object with handle ${handle} was not correctly stored.`);
		return true;
	}

	async encryptAndStoreObject<T>(handle: string, data: T, overwriteHash?: string) {
		// Encode the data
		const encoder = new TextEncoder();
		const encodedData = encoder.encode(JSON.stringify(data));
		// Encrypt the encoded data if the userSecret is present
		const userSecret = await this.getUserSecretInfo();
		// this._getUserSecret will only return undefined in case where the user gets logged out because they were messing with their local storage.
		// In practice, this means that this._getUserSecret will never return undefined, since the user will have been redirected to the login page in that case.
		assert.isDefined(userSecret, 'Could not retrieve the userSecret from localstorage.');
		const encodedUserSecret = new Uint8Array(Buffer.from(userSecret, 'base64'));
		const encryptedData = await this.encryptData(encodedData, encodedUserSecret);
		await this.storeObject(handle, encryptedData, overwriteHash);
	}

	// #region Hub Login

	async pppEP(): Promise<string | undefined> {
		const maxRetries = 1;
		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			const options = {
				headers: { Authorization: (await this._getAuthToken()) ?? '' },
				method: 'POST',
			};
			const okPppResp = await handleErrors<TPHC.PppResp>(() =>
				this._phcAPI.api<TPHC.PHCPppResp>(this._phcAPI.apiURLS.polymorphicPseudonymPackage, options),
			);
			if (okPppResp === 'RetryWithNewAuthToken') {
				if (attempt < maxRetries && (await this._refreshAndRetry())) {
					continue; // Retry with new token
				}
				return; // Logout was triggered or max retries reached
			} else if ('Success' in okPppResp) {
				return okPppResp.Success;
			} else {
				throw new Error('Unknown response from the ppp endpoint.');
			}
		}
	}

	async hhppEP(sealedEhpp: string, hhppSignatureScheme?: string): Promise<string | undefined> {
		const maxRetries = 1;
		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			const hhppReq: TPHC.HhppReq = { ehpp: sealedEhpp, hhpp_signature_scheme: hhppSignatureScheme };
			const options = {
				body: JSON.stringify(hhppReq),
				headers: {
					'Content-Type': 'application/json',
					Authorization: (await this._getAuthToken()) ?? '',
				},
				method: 'POST',
			};
			const okHhppResp = await handleErrors<TPHC.HhppResp>(() =>
				this._phcAPI.api<TPHC.PHCHhppResp>(this._phcAPI.apiURLS.hashedHubPseudonymPackage, options),
			);
			if (okHhppResp === 'RetryWithNewPpp') {
				return okHhppResp;
			} else if (okHhppResp === 'RetryWithNewAuthToken') {
				if (attempt < maxRetries && (await this._refreshAndRetry())) {
					continue; // Retry with new token
				}
				return; // Logout was triggered or max retries reached
			} else if ('Success' in okHhppResp) {
				return okHhppResp.Success;
			} else {
				throw new Error('Unknown response from the hhpp endpoint.');
			}
		}
	}

	// #endregion
}
